import { Separator } from '@/components/ui/separator';
import { measureConnectionTier, toDisplayTier } from '@/lib/connectionQuality';
import {
  Circle,
  CircleX,
  Loader2,
  Rocket,
  Settings,
  TriangleAlert,
  Wifi,
  WifiHigh,
  WifiLow,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PoweredByLogo } from '../public/PoweredByLogo';
import CamLayoutSettings from './CamLayoutSettings';
import UserGroupIcon from '@/components/icons/UserGroupIcon';
import { cn } from '@/lib/utils';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useWebinarT } from '@/hooks/useWebinarT';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import type { WebinarSession } from '@/features/webinar/types';
import { liveService } from '@/services/live.service';
import { toast } from 'react-toastify';
import { formatLiveDuration } from '@/lib/formatLiveDuration';

const QUIT_POPUP_MIN_DURATION_SEC = 2 * 60;

const RE_OPTIONAL_HTTP_PREFIX = /^https?:\/\/|^\/\//i;
const RE_REDIRECT_HOST =
  /^(?:localhost|\[[^\]]+\]|\d{1,3}(?:\.\d{1,3}){3}|[a-z0-9][-a-z0-9.]*\.[a-z]{2,})$/i;
const RE_AUTHORITY_AND_REST = /^([^/?#]+)([\s\S]*)$/;

function isValidCriticalBackupUrl(raw: string): boolean {
  const s = raw.trim();
  if (!s) return false;
  if (/^(javascript:|data:|vbscript:)/i.test(s)) return false;

  const rest = s.replace(RE_OPTIONAL_HTTP_PREFIX, '');
  const m = RE_AUTHORITY_AND_REST.exec(rest);
  if (!m) return false;

  const host = m[1].replace(/:\d+$/, '');
  const afterAuthority = m[2] ?? '';

  if (!RE_REDIRECT_HOST.test(host)) return false;
  return !/\s/.test(afterAuthority);
}

function normalizeCriticalRedirectUrl(raw: string): string {
  const s = raw.trim();
  if (/^https?:\/\//i.test(s)) return s;
  if (/^\/\//i.test(s)) return `https:${s}`;
  return `https://${s}`;
}

export type ChatVisibility = 'PUBLIC' | 'PRIVATE' | 'DISABLED';

export interface LiveSettings {
  displayParticipantsMinThreshold: number;
  chatMode: ChatVisibility;
  displayParticipantsCountEnabled: boolean;
}

interface FooterBarProps {
  handleStartSession?: () => void;
  liveStartAt?: string;
  liveParticipantCount?: number;
  handleCloseSession?: () => void | Promise<void>;
  startSessionLoading?: boolean;
  launchPending?: boolean;
  startButtonDisabledByOtherAdmin?: boolean;
  startButtonDisabledByConfigurationOnly?: boolean;
  isAttendee?: boolean;
  visualIdentity?: WorkspaceVisualIdentity;
  displayParticipantsMinThreshold?: number;
  displayParticipantsCountEnabled?: boolean;
  onLiveDurationReached2Min?: (reached: boolean) => void;
  isModeratorLive?: boolean;
  formattedPlannedDate?: string;
  isActive?: boolean;
  showSettings?: boolean;
  settings?: LiveSettings;
  onSettingsChange?: (settings: LiveSettings) => void;
  chatModeUpdating?: boolean;
  session?: WebinarSession;
  isEvergreen?: boolean;
  evergreenElapsedDisplaySeconds?: number;
  // Compte gratuit sans accès live : le parent fournit ici le CTA d'upgrade
  // (parcours d'abonnement) qui remplace le bouton « Démarrer la session ».
  upgradeSlot?: ReactNode;
}

export default function FooterBar({
  handleStartSession,
  liveStartAt,
  liveParticipantCount = 0,
  startSessionLoading,
  launchPending,
  startButtonDisabledByOtherAdmin,
  startButtonDisabledByConfigurationOnly,
  isAttendee,
  handleCloseSession,
  visualIdentity,
  displayParticipantsMinThreshold,
  displayParticipantsCountEnabled = true,
  onLiveDurationReached2Min,
  isModeratorLive,
  formattedPlannedDate,
  isActive,
  showSettings,
  settings,
  onSettingsChange,
  chatModeUpdating,
  session,
  isEvergreen,
  evergreenElapsedDisplaySeconds,
  upgradeSlot,
}: FooterBarProps) {
  const { t } = useWebinarT('common');
  const { t: tw } = useWebinarT('webinars');
  const tFootbar = (key: string, opts?: Record<string, unknown>) =>
    tw(`${isAttendee ? 'live' : 'adminLive'}.footbar.${key}`, opts);
  const [duration, setDuration] = useState('00:00');
  const [connectionQuality, setConnectionQuality] = useState<'high' | 'medium' | 'low'>('high');
  const [isClosing, setIsClosing] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [criticalBackupUrl, setCriticalBackupUrl] = useState('');
  const [criticalRedirectOpen, setCriticalRedirectOpen] = useState(false);
  const [criticalRedirectSubmitting, setCriticalRedirectSubmitting] = useState(false);
  const shouldShowParticipantCount =
    !isAttendee ||
    (isAttendee &&
      displayParticipantsCountEnabled &&
      displayParticipantsMinThreshold !== undefined &&
      liveParticipantCount >= displayParticipantsMinThreshold);

  useEffect(() => {
    if (!liveStartAt) {
      onLiveDurationReached2Min?.(false);
      return;
    }

    const useVideoElapsed =
      isEvergreen === true &&
      evergreenElapsedDisplaySeconds !== undefined &&
      Number.isFinite(evergreenElapsedDisplaySeconds);

    if (useVideoElapsed) {
      const secs = Math.max(0, Math.floor(evergreenElapsedDisplaySeconds));
      setDuration(formatLiveDuration(secs));
      onLiveDurationReached2Min?.(secs >= QUIT_POPUP_MIN_DURATION_SEC);
      return;
    }

    const updateDuration = () => {
      const now = new Date();
      const utcString = liveStartAt.endsWith('Z')
        ? liveStartAt
        : `${liveStartAt.replace(/Z?$/, '')}Z`;
      const startTime = new Date(utcString);
      const diffInSeconds = Math.floor((now.getTime() - startTime.getTime()) / 1000);

      setDuration(formatLiveDuration(diffInSeconds));

      onLiveDurationReached2Min?.(diffInSeconds >= QUIT_POPUP_MIN_DURATION_SEC);
    };

    updateDuration();

    const interval = setInterval(updateDuration, 1000);

    return () => clearInterval(interval);
  }, [liveStartAt, onLiveDurationReached2Min, isEvergreen, evergreenElapsedDisplaySeconds]);

  useEffect(() => {
    const testConnection = async () => {
      try {
        const quality = await measureConnectionTier();
        // Sonde non concluante (bloqueur) : garder le badge affiché plutôt que annoncer « faible ».
        if (quality !== null) setConnectionQuality(toDisplayTier(quality));
      } catch (error) {
        console.error('Erreur lors du test de connexion:', error);
        setConnectionQuality('low');
      }
    };

    testConnection();

    const interval = setInterval(testConnection, 30000);

    return () => clearInterval(interval);
  }, []);

  const renderWifiIcon = () => {
    const iconProps = { className: 'size-4 xl:size-5' };

    switch (connectionQuality) {
      case 'high':
        return <Wifi {...iconProps} color="green" />;
      case 'medium':
        return <WifiHigh {...iconProps} color="green" />;
      case 'low':
        return <WifiLow {...iconProps} color="red" />;
      default:
        return <Wifi {...iconProps} color="green" />;
    }
  };

  const isDark = visualIdentity?.theme === 'DARK';
  const bgClass = isDark ? 'bg-[var(--color-dark)]' : 'bg-white';
  const borderClass = isDark ? 'border-[var(--color-card-inverse)]' : 'border-[var(--color-card)]';
  const separatorBgClass = isDark ? 'bg-[var(--color-card-inverse)]' : 'bg-[var(--color-card)]';
  const textClass = isDark ? 'text-white' : 'text-[var(--color-secondary)]';

  return (
    <div className={`flex-row w-full items-center 2xl:items-end flex justify-between gap-2 h-full`}>
      <div className="hidden lg:block">
        <PoweredByLogo
          theme={visualIdentity?.theme as 'LIGHT' | 'DARK'}
          classNameText="text-xs 2xl:text-base"
          classNameLogo="w-16"
          isModeratorLive={isModeratorLive}
          displayItemsOnModeratorLive={isActive}
        />
      </div>

      <div
        className={cn(
          // h-8
          'h-full p-2 px-2 ps-4 w-full md:w-full lg:w-auto shadow-card flex items-center justify-center self-end border',
          (isModeratorLive && isEvergreen) || (isModeratorLive && 'pe-4'),
          bgClass,
          borderClass,
          isModeratorLive && isActive
            ? 'flex-col rounded-3xl gap-2'
            : 'rounded-none flex-row md:flex-row 2xl:flex-row gap-2 2xl:gap-4 lg:rounded-full',
        )}
      >
        <div className="flex items-center gap-4 h-full">
          <div>
            <p
              className={cn('flex items-center justify-center gap-2 text-xs xl:text-sm', textClass)}
            >
              {liveStartAt ? (
                <>
                  <Circle className="w-4 h-4" fill="red" stroke="red" />
                  <span className="hidden sm:block lg:hidden xl:block">
                    {' '}
                    {tFootbar('liveNow')} -{' '}
                  </span>{' '}
                  {duration}
                </>
              ) : (
                <>
                  <Circle className="w-4 h-4" fill="gray" stroke="gray" />
                  <span className="hidden lg:block"> {tFootbar('offline')}</span>
                </>
              )}
            </p>
          </div>
          <Separator orientation="vertical" className={cn('h-full', separatorBgClass)} />
          <p className={`${textClass} text-xs xl:text-sm`}>
            {tFootbar('liveDate', { date: formattedPlannedDate })}
          </p>
        </div>

        <Separator
          orientation="horizontal"
          className={cn(
            'w-full',
            isModeratorLive && isActive ? 'hidden md:block' : 'hidden md:hidden 2xl:hidden',
            separatorBgClass,
          )}
        />

        <div className="flex items-center h-full gap-4">
          {shouldShowParticipantCount && (
            <>
              <Separator
                orientation="vertical"
                className={cn(
                  'h-full',
                  isModeratorLive && isActive ? 'hidden' : 'hidden 2xl:block',
                  separatorBgClass,
                )}
              />

              <div className="flex items-center gap-2">
                <UserGroupIcon className="size-4 md:size-5" />
                <p className={`${textClass} text-xs xl:text-sm`}>{liveParticipantCount}</p>
              </div>
            </>
          )}

          <Separator orientation="vertical" className={cn('h-full', separatorBgClass)} />

          {showSettings && settings && onSettingsChange && (
            <>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'flex items-center gap-2 cursor-pointer p-1 rounded hover:opacity-80',
                      textClass,
                    )}
                    aria-label={t('liveSettings.title')}
                  >
                    <Settings className="size-4 xl:size-5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto max-w-[min(640px,calc(100vw-2rem))]"
                  side="top"
                  align="end"
                  sideOffset={8}
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <div className="flex flex-col gap-5">
                    <div className="flex flex-row gap-2">
                      <Label className="text-base w-full">{t('liveSettings.chatVisibility')}</Label>
                      <Select
                        value={settings.chatMode}
                        disabled={chatModeUpdating}
                        onValueChange={(value) =>
                          onSettingsChange({
                            ...settings,
                            chatMode: value as ChatVisibility,
                          })
                        }
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PUBLIC">{t('liveSettings.chatPublic')}</SelectItem>
                          <SelectItem value="PRIVATE">{t('liveSettings.chatPrivate')}</SelectItem>
                          <SelectItem value="DISABLED">{t('liveSettings.chatDisabled')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {!isEvergreen && <CamLayoutSettings />}
                    <div className="flex flex-row justify-between gap-2">
                      <Label
                        htmlFor="participants-threshold"
                        className="text-base inline leading-4"
                      >
                        {t('liveSettings.participantsDisplay')}{' '}
                        <span className="text-xs font-normal">
                          ({t('liveSettings.participantsDisplaySmall')})
                        </span>
                      </Label>
                      <Switch
                        id="participants-threshold"
                        checked={settings.displayParticipantsCountEnabled}
                        onCheckedChange={(checked) => {
                          onSettingsChange({
                            ...settings,
                            displayParticipantsCountEnabled: checked,
                          });
                        }}
                      />
                    </div>
                    <div className="flex flex-row justify-between items-center gap-2">
                      <Label
                        htmlFor="critical-redirection"
                        className="text-base leading-4 flex items-center gap-2"
                      >
                        <TriangleAlert className="size-5 text-red-500" />
                        {t('liveSettings.criticalRedirection')}{' '}
                      </Label>
                      <Dialog open={criticalRedirectOpen} onOpenChange={setCriticalRedirectOpen}>
                        <DialogTrigger asChild>
                          <Button>{t('liveSettings.criticalRedirectionButton')}</Button>
                        </DialogTrigger>

                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>{t('liveSettings.criticalRedirectionTitle')}</DialogTitle>
                          </DialogHeader>
                          <DialogDescription>
                            {t('liveSettings.criticalRedirectionDescription1Before')}
                            <span className="font-bold">
                              {t('liveSettings.criticalRedirectionDescription1Bold')}
                            </span>
                            {t('liveSettings.criticalRedirectionDescription1After')}
                          </DialogDescription>
                          <DialogDescription>
                            <span className="text-black font-medium">
                              {t('liveSettings.criticalRedirectionDescription2Bold')}
                            </span>
                            {t('liveSettings.criticalRedirectionDescription2')}
                          </DialogDescription>
                          <div className="flex flex-col gap-1">
                            <Input
                              disabled={
                                session?.status !== 'IN_PROGRESS' ||
                                isClosing ||
                                criticalRedirectSubmitting
                              }
                              placeholder={t('liveSettings.criticalRedirectionUrlPlaceholder')}
                              className={cn(
                                'h-12',
                                criticalBackupUrl.trim() &&
                                  !isValidCriticalBackupUrl(criticalBackupUrl) &&
                                  'border-destructive focus-visible:ring-destructive',
                              )}
                              aria-invalid={
                                !!(
                                  criticalBackupUrl.trim() &&
                                  !isValidCriticalBackupUrl(criticalBackupUrl)
                                )
                              }
                              value={criticalBackupUrl}
                              onChange={(e) => setCriticalBackupUrl(e.target.value)}
                            />
                            {criticalBackupUrl.trim() &&
                              !isValidCriticalBackupUrl(criticalBackupUrl) && (
                                <p className="text-sm text-destructive" role="alert">
                                  {t('liveSettings.criticalRedirectionUrlInvalid')}
                                </p>
                              )}
                          </div>
                          {session?.status !== 'IN_PROGRESS' && (
                            <div className="p-2 bg-warning/20 border border-warning rounded-md">
                              <p className="text-gray-800 text-sm font-medium">
                                {t('liveSettings.criticalRedirectionWarning')}
                              </p>
                            </div>
                          )}
                          <DialogFooter className="flex justify-center gap-2 mt-6 w-full">
                            <DialogClose asChild>
                              <Button variant="outline" className="flex-1">
                                {t('liveSettings.criticalRedirectionCancelButton')}
                              </Button>
                            </DialogClose>
                            <Button
                              type="button"
                              disabled={
                                criticalRedirectSubmitting ||
                                isClosing ||
                                !isValidCriticalBackupUrl(criticalBackupUrl) ||
                                session?.status !== 'IN_PROGRESS'
                              }
                              className="flex-1"
                              onClick={async () => {
                                if (
                                  !session ||
                                  !isValidCriticalBackupUrl(criticalBackupUrl) ||
                                  session.status !== 'IN_PROGRESS'
                                ) {
                                  return;
                                }
                                setCriticalRedirectSubmitting(true);
                                setIsClosing(true);
                                try {
                                  const urlToSend = normalizeCriticalRedirectUrl(criticalBackupUrl);
                                  await liveService.redirectLiveSession(
                                    session.workspaceId,
                                    session.webinarId,
                                    session.id,
                                    urlToSend,
                                  );
                                  toast.success(t('liveSettings.criticalRedirectionSuccess'));
                                  setCriticalBackupUrl('');

                                  handleCloseSession?.();
                                  // Seulement après un appel réussi : sur un refus (le back exige
                                  // un admin du workspace), partir quand même sortait du live celui
                                  // qui a cliqué sans que personne d'autre ne soit redirigé.
                                  window.location.replace(urlToSend);
                                } catch {
                                  toast.error(t('liveSettings.criticalRedirectionError'));
                                  setIsClosing(false);
                                } finally {
                                  setCriticalRedirectSubmitting(false);
                                }
                              }}
                            >
                              {criticalRedirectSubmitting || isClosing ? (
                                <Loader2 className="animate-spin size-4" />
                              ) : (
                                t('liveSettings.criticalRedirectionConfirmButton')
                              )}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <Separator orientation="vertical" className={cn('h-full', separatorBgClass)} />
            </>
          )}

          {renderWifiIcon()}

          {!isAttendee && !isEvergreen && !isModeratorLive && (
            <>
              <Separator orientation="vertical" className={cn('h-full', separatorBgClass)} />
              {liveStartAt ? (
                <Dialog
                  open={closeDialogOpen}
                  onOpenChange={(open) => {
                    setCloseDialogOpen(open);
                    if (!open) {
                      setIsClosing(false);
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <div
                      className={cn(
                        'flex items-center gap-2 pe-2 cursor-pointer',
                        isClosing ? 'opacity-50 cursor-not-allowed' : '',
                      )}
                    >
                      <CircleX size={18} color="red" />
                      <p className="text-red-500 text-xs xl:text-sm">
                        {t('liveSettings.closeSessionButton')}
                      </p>
                    </div>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('liveSettings.closeSessionTitle')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4">
                      <p className="text-text-secondary">
                        {t('liveSettings.closeSessionDescription')}
                      </p>
                      <p className="text-text-secondary">
                        {t('liveSettings.closeSessionDescription2')}{' '}
                        <span className="text-black font-bold">
                          {t('liveSettings.closeSessionDescription2Bold')}
                        </span>{' '}
                        {t('liveSettings.closeSessionDescription2After')}
                      </p>
                    </div>

                    <DialogFooter className="flex justify-center gap-2 w-full">
                      <DialogClose asChild>
                        <Button variant="outline" className="w-1/2" disabled={isClosing}>
                          {t('liveSettings.closeSessionCancelButton')}
                        </Button>
                      </DialogClose>
                      <Button
                        type="button"
                        className="w-1/2 inline-flex items-center justify-center gap-2"
                        disabled={isClosing}
                        onClick={() => {
                          setIsClosing(true);
                          void Promise.resolve(handleCloseSession?.()).finally(() => {
                            setIsClosing(false);
                          });
                        }}
                      >
                        {isClosing ? (
                          <>
                            <Loader2 className="animate-spin size-4 shrink-0 inline mr-2 align-middle" />
                            {t('liveSettings.closeSessionClosing')}
                          </>
                        ) : (
                          t('liveSettings.closeSessionConfirmButton')
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              ) : upgradeSlot ? (
                upgradeSlot
              ) : (
                <Dialog
                  open={startDialogOpen}
                  onOpenChange={(open) => {
                    setStartDialogOpen(open);
                    if (!open) {
                      setIsStarting(false);
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button
                      disabled={
                        startSessionLoading ||
                        launchPending ||
                        startButtonDisabledByOtherAdmin ||
                        startButtonDisabledByConfigurationOnly ||
                        !!liveStartAt ||
                        isStarting
                      }
                      className="text-xs xl:text-sm"
                    >
                      {startSessionLoading || isStarting || launchPending ? (
                        <Loader2 className="animate-spin" size={16} />
                      ) : (
                        <Rocket size={16} />
                      )}{' '}
                      {t('liveSettings.startSessionButton')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('liveSettings.startSessionTitle')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4">
                      <p className="text-text-secondary">
                        {t('liveSettings.startSessionDescription')}
                      </p>
                      <p className="text-text-secondary">
                        {t('liveSettings.startSessionDescription2')}{' '}
                        <span className="text-black font-bold">
                          {t('liveSettings.startSessionDescription2Bold')}
                        </span>{' '}
                        {t('liveSettings.startSessionDescription2After')}
                      </p>
                    </div>
                    <DialogFooter className="flex justify-center gap-2 w-full">
                      <DialogClose asChild>
                        <Button variant="outline" className="w-1/2" disabled={isStarting}>
                          {t('liveSettings.startSessionCancelButton')}
                        </Button>
                      </DialogClose>
                      <Button
                        type="button"
                        className="w-1/2 inline-flex items-center justify-center gap-2"
                        disabled={
                          isStarting ||
                          startSessionLoading ||
                          startButtonDisabledByConfigurationOnly
                        }
                        onClick={() => {
                          setStartDialogOpen(false);
                          setIsStarting(true);
                          void Promise.resolve(handleStartSession?.()).finally(() => {
                            setIsStarting(false);
                          });
                        }}
                      >
                        {isStarting || startSessionLoading ? (
                          <>
                            <Loader2 className="animate-spin size-4 shrink-0 inline mr-2 align-middle" />
                            {t('liveSettings.startSessionStarting')}
                          </>
                        ) : (
                          t('liveSettings.startSessionConfirmButton')
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
